import React, { createContext, useContext, useMemo, useCallback, useState, useEffect } from 'react';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useBooks } from '../hooks/useBooks';
import { useRecords } from '../hooks/useRecords';
import { useShelves } from '../hooks/useShelves';
import { useReadingStreak } from '../hooks/useReadingStreak';
import { useCoaching } from '../hooks/useCoaching';
import { getKoreanToday } from '../utils/dateUtils';
import { buildTotalPagesChange } from '../utils/rescaleUtils';
import { CERT_TRACKS } from '../utils/certification';
import { applyLegacyKdc } from '../utils/kdc';
import { saveBookSnapshot } from '../utils/sessionCache';
import { DEFAULT_NICKNAME, INITIAL_DAYS_KOREAN, INITIAL_DAYS_ENGLISH } from '../constants';

// 누적 독서일이 담긴 레거시 단일 문서 (users/{uid} 가 아니라 'userId' 라는 고정 id)
const SETTINGS_DOC = ['users', 'userId'];

const DEFAULT_USER_SETTINGS = {
  nickname: DEFAULT_NICKNAME,
  accumulatedDaysKorean: INITIAL_DAYS_KOREAN,
  accumulatedDaysEnglish: INITIAL_DAYS_ENGLISH,
  lastCertifiedDateKorean: null,
  lastCertifiedDateEnglish: null,
  onReadingComplete: null
};

const AppDataContext = createContext(null);

/**
 * 앱 전역 독서 데이터 공급자.
 * 이전에는 App.jsx 와 ReadingProgressDashboard.jsx 가 각각 useBooks 를 호출해
 * Firestore 구독이 두 번 열렸다. 여기서 한 번만 구독하고 모든 페이지가 공유한다.
 */
export const AppDataProvider = ({ user, onLogout, children }) => {
  const {
    books,
    completedBooks,
    loading,
    calculateTodayStats,
    addBook,
    updateBook,
    deleteBook,
    updateReadingProgress,
    setBookKdc: setBookKdcIn
  } = useBooks(user?.uid);

  // 다음에 공유로 열릴 때 로그인 확인 전에 책을 고를 수 있게, 책 목록 사본을 남긴다 (utils/sessionCache)
  useEffect(() => {
    if (!loading) saveBookSnapshot(books);
  }, [books, loading]);

  const {
    records,
    recordsLoading,
    addRecord,
    updateRecord,
    deleteRecord,
    deleteRecordsForBook,
    rescaleRecordPages
  } = useRecords(user?.uid, books);

  const { shelves, addShelf, renameShelf, deleteShelf } = useShelves(user?.uid);

  const {
    currentStreak,
    totalReadingDays,
    updateStreak,
    lastReadDate,
    readDates
  } = useReadingStreak(user?.uid);

  const streakData = useMemo(() => ({
    currentStreak,
    totalReadingDays,
    lastReadDate,
    readDates
  }), [currentStreak, totalReadingDays, lastReadDate, readDates]);

  // useCoaching 은 매 렌더마다 새 객체를 반환하므로 필드 단위로 다시 메모이즈한다
  const {
    message: coachingMessage,
    loading: coachingLoading,
    dismissed: coachingDismissed,
    dismissCoaching,
    triggerFeedback,
    refreshCoaching
  } = useCoaching(books, streakData, completedBooks, records);

  const coaching = useMemo(() => ({
    message: coachingMessage,
    loading: coachingLoading,
    dismissed: coachingDismissed,
    dismissCoaching,
    triggerFeedback,
    refresh: refreshCoaching
  }), [
    coachingMessage, coachingLoading, coachingDismissed,
    dismissCoaching, triggerFeedback, refreshCoaching
  ]);

  // 누적 독서일 / 닉네임 — UserSettings 컴포넌트와 같은 문서를 읽는다.
  // (설정 화면에서 값을 바꾸면 setUserSettings 로 즉시 동기화된다)
  const [userSettings, setUserSettings] = useState(DEFAULT_USER_SETTINGS);

  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;

    getDoc(doc(db, ...SETTINGS_DOC))
      .then(snap => {
        if (cancelled || !snap.exists()) return;
        const data = snap.data();
        setUserSettings(prev => ({
          ...prev,
          nickname: data.nickname || prev.nickname,
          accumulatedDaysKorean: data.accumulatedDaysKorean ?? prev.accumulatedDaysKorean,
          accumulatedDaysEnglish: data.accumulatedDaysEnglish ?? prev.accumulatedDaysEnglish,
          lastCertifiedDateKorean: data.lastCertifiedDateKorean ?? prev.lastCertifiedDateKorean,
          lastCertifiedDateEnglish: data.lastCertifiedDateEnglish ?? prev.lastCertifiedDateEnglish
        }));
      })
      .catch(err => console.error('Failed to load user settings:', err));

    return () => { cancelled = true; };
  }, [user?.uid]);

  const today = getKoreanToday();

  /**
   * 설정 화면에서 넘어오는 값은 일부 필드만 담고 있다.
   * 통째로 갈아끼우면 인증 날짜 같은 필드가 지워져 같은 날 두 번 세게 되므로 병합한다.
   */
  const patchUserSettings = useCallback((patch) => {
    setUserSettings(prev => ({ ...prev, ...patch }));
  }, []);

  /**
   * 화면에 보이는 이름 바꾸기 (인사말·사이드바).
   * 독서 인증에 찍히는 필명은 CERTIFICATION_NAME 으로 따로 고정되어 있어 여기 영향을 받지 않는다.
   */
  const changeNickname = useCallback(async (next) => {
    const nickname = String(next || '').trim();
    if (!nickname) throw new Error('이름은 비워둘 수 없어요.');

    const previous = userSettings.nickname;
    setUserSettings(prev => ({ ...prev, nickname }));
    try {
      await setDoc(doc(db, ...SETTINGS_DOC), { nickname }, { merge: true });
    } catch (error) {
      setUserSettings(prev => ({ ...prev, nickname: previous }));
      throw error;
    }
    return nickname;
  }, [userSettings.nickname]);

  /**
   * 독서 인증 복사 = 그날의 인증. 누적 인증일수를 1 올리고 날짜를 남긴다.
   * 같은 날 여러 번 눌러도 하루는 하루이므로 날짜로 막는다.
   */
  const certifyReading = useCallback(async (track = 'korean') => {
    const fields = CERT_TRACKS[track] || CERT_TRACKS.korean;
    const certDay = getKoreanToday();

    if (userSettings[fields.date] === certDay) {
      return { counted: false, day: userSettings[fields.days] || 0 };
    }

    const next = (userSettings[fields.days] || 0) + 1;
    const changes = { [fields.days]: next, [fields.date]: certDay };

    // 낙관적 반영 — 실패하면 되돌린다
    setUserSettings(prev => ({ ...prev, ...changes }));
    try {
      await setDoc(doc(db, ...SETTINGS_DOC), changes, { merge: true });
    } catch (error) {
      setUserSettings(prev => ({
        ...prev,
        [fields.days]: userSettings[fields.days] || 0,
        [fields.date]: userSettings[fields.date] || null
      }));
      throw error;
    }
    return { counted: true, day: next };
  }, [userSettings]);

  const todayStats = useMemo(
    () => (loading ? { pages: 0, minutes: 0, booksRead: 0 } : calculateTodayStats(today)),
    [loading, calculateTodayStats, today]
  );

  /** 진도 기록 + 스트릭 갱신 + AI 코칭 피드백 (기존 대시보드 로직 이관) */
  const recordProgress = useCallback(async (bookId, pages) => {
    const result = await updateReadingProgress(bookId, pages);

    if (result) {
      const updatedBooks = books.map(book =>
        book.id === result.bookId
          ? { ...book, readingHistory: result.updatedReadingHistory }
          : book
      );
      await updateStreak(result.today, updatedBooks, completedBooks);
    }

    setTimeout(() => triggerFeedback('progress_update'), 500);
    return result;
  }, [updateReadingProgress, updateStreak, books, completedBooks, triggerFeedback]);

  /**
   * 전체 페이지 수 변경.
   *
   * 전자책은 배율에 따라 총 쪽수가 달라져서, 단순히 분모만 바꾸면
   * 이미 쌓인 일별 독서량과 인용 페이지가 옛 척도로 남아 어긋난다.
   * mode='rescale' 이면 기록까지 비례 이동하고, 'correct' 면 분모만 바꾼다.
   */
  const changeTotalPages = useCallback(async (bookId, newTotalPages, mode) => {
    const book = books.find(b => b.id === bookId);
    if (!book) throw new Error('책을 찾을 수 없습니다.');
    if (!Number.isFinite(newTotalPages) || newTotalPages < 1) {
      throw new Error('전체 페이지는 1쪽 이상이어야 합니다.');
    }

    const { changes, ratio } = buildTotalPagesChange(book, newTotalPages, mode);

    // 책을 먼저 바꾼다. 기록 페이지는 실패해도 되돌릴 수 있는 부수 작업이다.
    await updateBook(bookId, changes);

    let movedRecords = 0;
    if (mode === 'rescale' && ratio !== 1) {
      movedRecords = await rescaleRecordPages(bookId, ratio, newTotalPages);
    }
    return { ratio, movedRecords };
  }, [books, updateBook, rescaleRecordPages]);

  /** 기록을 남기면 코치도 그 내용을 보고 반응하도록 트리거한다 */
  const addRecordWithFeedback = useCallback(async (payload) => {
    const id = await addRecord(payload);
    setTimeout(() => triggerFeedback('record_added'), 500);
    return id;
  }, [addRecord, triggerFeedback]);

  const addBookForUser = useCallback(async (bookData) => {
    if (!user?.uid) throw new Error('로그인이 필요합니다.');
    return addBook({ ...bookData, userId: user.uid });
  }, [addBook, user?.uid]);

  /** 책 삭제 시 그 책의 기록도 함께 정리한다 */
  /**
   * 예전 완독 책의 분야.
   *
   * completedBooks 는 읽기 전용 레거시라 그 문서에 쓰지 않는다. 실제로 그 컬렉션에
   * 쓰기를 시도했더니 98권이 전부 거부됐다 (이 컬렉션은 만들어진 뒤 한 번도
   * 고쳐 쓴 적이 없다). 그래서 분야는 사용자 문서에 { 책id: 분야 } 로 따로 적고,
   * 읽을 때 각 책에 덧입힌다.
   */
  const [legacyKdc, setLegacyKdc] = useState({});

  useEffect(() => {
    if (!user?.uid) return undefined;
    return onSnapshot(
      doc(db, 'users', user.uid),
      snap => setLegacyKdc(snap.data()?.legacyBookKdc || {}),
      error => console.error('Failed to load legacy categories:', error)
    );
  }, [user?.uid]);

  const completedBooksWithKdc = useMemo(
    () => applyLegacyKdc(completedBooks, legacyKdc),
    [completedBooks, legacyKdc]
  );

  /** 책의 분야를 정한다. 지금 서재의 책은 책 문서에, 예전 완독 책은 사용자 문서에 적는다 */
  const setBookKdc = useCallback(async (bookId, value) => {
    if (books.some(book => book.id === bookId)) {
      return setBookKdcIn(bookId, value, 'books');
    }
    if (!user?.uid) throw new Error('로그인이 필요합니다.');

    const kdc = value.kdc || null;
    const kdcDivision = kdc && value.kdcDivision && value.kdcDivision[0] === String(kdc)
      ? value.kdcDivision
      : null;
    const kdcSource = kdc
      ? (value.kdcSource || 'manual')
      : (value.kdcSource === 'notfound' ? 'notfound' : null);
    const kdcClassNo = kdc && kdcSource !== 'manual' ? (value.kdcClassNo || null) : null;

    // 이 책 하나의 키만 병합한다 (다른 책의 분야를 건드리지 않는다)
    await setDoc(
      doc(db, 'users', user.uid),
      { legacyBookKdc: { [bookId]: { kdc, kdcDivision, kdcClassNo, kdcSource } } },
      { merge: true }
    );
    return undefined;
  }, [books, setBookKdcIn, user?.uid]);

  const removeBook = useCallback(async (bookId, keepReadingHistory = true) => {
    await deleteBook(bookId, keepReadingHistory);
    try {
      await deleteRecordsForBook(bookId);
    } catch (error) {
      console.error('Failed to delete records for book:', error);
    }
  }, [deleteBook, deleteRecordsForBook]);

  /** bookId → 기록 배열 */
  const recordsByBook = useMemo(() => {
    const map = new Map();
    records.forEach(record => {
      const bucket = map.get(record.bookId);
      if (bucket) bucket.push(record);
      else map.set(record.bookId, [record]);
    });
    return map;
  }, [records]);

  const recordCounts = useMemo(() => {
    // KST 날짜 문자열 기준으로만 계산한다 (UTC 변환을 섞으면 하루가 어긋난다)
    const [y, m, d] = today.split('-').map(Number);
    const start = new Date(Date.UTC(y, m - 1, d));
    start.setUTCDate(start.getUTCDate() - 6);
    const weekStart = start.toISOString().split('T')[0];

    return records.reduce((acc, record) => {
      if (record.date === today) acc.today += 1;
      if (record.date >= weekStart) acc.week += 1;
      return acc;
    }, { today: 0, week: 0 });
  }, [records, today]);

  const value = useMemo(() => ({
    user,
    onLogout,
    books,
    // 분야를 덧입힌 목록을 내보낸다. 원본 문서는 그대로다
    completedBooks: completedBooksWithKdc,
    loading,
    today,
    todayStats,
    addBook: addBookForUser,
    updateBook,
    changeTotalPages,
    deleteBook: removeBook,
    setBookKdc,
    recordProgress,
    streak: streakData,
    coaching,
    userSettings,
    setUserSettings: patchUserSettings,
    changeNickname,
    certifyReading,
    // 기록
    records,
    recordsByBook,
    recordCounts,
    recordsLoading,
    addRecord: addRecordWithFeedback,
    updateRecord,
    deleteRecord,
    // 책장
    shelves,
    addShelf,
    renameShelf,
    deleteShelf
  }), [
    user, onLogout, books, completedBooksWithKdc, loading, today, todayStats,
    addBookForUser, updateBook, changeTotalPages, removeBook, recordProgress, streakData, coaching,
    userSettings, patchUserSettings, changeNickname, certifyReading,
    records, recordsByBook, recordCounts, recordsLoading,
    addRecordWithFeedback, updateRecord, deleteRecord, setBookKdc,
    shelves, addShelf, renameShelf, deleteShelf
  ]);

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
};

export const useAppData = () => {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used within AppDataProvider');
  return ctx;
};
